import { useState, useEffect, useRef } from "react";

/**
 * useVersionCheck
 *
 * Polls /version.json at a set interval and signals when a newer version
 * has been deployed.
 *
 * @param {number} intervalMs - How often to poll (default: 30 minutes)
 * @param {number} autoReloadMs - How long to wait after showing the banner
 *                                before auto-reloading. Set to null to disable.
 *                                (default: 30 seconds)
 */
export function useVersionCheck(
  intervalMs = 30 * 60 * 1000,
  autoReloadMs = 30_000
) {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const initialVersion = useRef(null);
  const autoReloadTimer = useRef(null);

  const fetchVersion = async () => {
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, {
        cache: "no-store",
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data?.version ?? null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    let pollInterval;

    const init = async () => {
      const version = await fetchVersion();
      if (version) {
        initialVersion.current = version;
      }

      pollInterval = setInterval(async () => {
        const latestVersion = await fetchVersion();
        if (
          latestVersion &&
          initialVersion.current &&
          latestVersion !== initialVersion.current
        ) {
          setUpdateAvailable(true);
        }
      }, intervalMs);
    };

    init();

    return () => {
      clearInterval(pollInterval);
      if (autoReloadTimer.current) clearTimeout(autoReloadTimer.current);
    };
  }, [intervalMs]);

  // Start auto-reload countdown once the banner is shown
  useEffect(() => {
    if (updateAvailable && autoReloadMs !== null) {
      autoReloadTimer.current = setTimeout(() => {
        window.location.reload();
      }, autoReloadMs);
    }
    return () => {
      if (autoReloadTimer.current) clearTimeout(autoReloadTimer.current);
    };
  }, [updateAvailable, autoReloadMs]);

  const dismissUpdate = () => {
    if (autoReloadTimer.current) clearTimeout(autoReloadTimer.current);
    setUpdateAvailable(false);
  };

  return { updateAvailable, dismissUpdate };
}
